<template>
  <v-app :theme="currentVuetifyTheme">
    <v-navigation-drawer v-model="drawer" temporary class="bg-surface backdrop-blur" width="280">
      <div class="pa-4 pt-6">

        <!-- Quick nav links on top -->
        <v-list density="compact" bg-color="transparent" class="pa-0 mb-2">
          <v-list-item prepend-icon="mdi-home" title="Feed" :active="route.path === '/'" @click="router.push('/'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-star-outline" title="Curated Feed" :active="route.path === '/curated'" @click="router.push('/curated'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-book-open-variant-outline" title="Stories" :active="route.path.includes('/note/')" @click="showMyNotes(); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-domain" title="Studios" :active="route.path === '/studios'" @click="router.push('/studios'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item v-if="showTimeline" prepend-icon="mdi-timeline-outline" title="Timeline" :active="route.path === '/timeline'" @click="router.push('/timeline'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item prepend-icon="mdi-calendar-outline" title="Events" :active="route.path === '/events'" @click="router.push('/events'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item v-if="user" prepend-icon="mdi-bookmark-outline" title="Bookmarks" :active="route.path === '/bookmarks'" @click="router.push('/bookmarks'); drawer = false" class="rounded-lg mb-1"></v-list-item>
          <v-list-item v-if="user" prepend-icon="mdi-playlist-play" title="Lists" :active="route.path === '/lists'" @click="router.push('/lists'); drawer = false" class="rounded-lg mb-1"></v-list-item>
        </v-list>

        <v-divider class="my-3 border-white-05"></v-divider>

        <!-- Collapsible Discover — styled exactly like other v-list-items -->
        <v-list density="compact" bg-color="transparent" class="pa-0 mb-1">
          <v-list-item
            prepend-icon="mdi-pound"
            title="Discover"
            class="rounded-lg"
            style="user-select: none; cursor: pointer;"
            @click="mobileDiscoverOpen = !mobileDiscoverOpen"
          >
            <template v-slot:append>
              <v-icon :icon="mobileDiscoverOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" size="18" color="grey"></v-icon>
            </template>
          </v-list-item>
        </v-list>

        <v-expand-transition>
          <div v-if="mobileDiscoverOpen">
            <!-- indent = 16px (drawer padding) + 24px (icon) + 16px (gap) = 56px, matching v-list-item text start -->
            <v-list density="compact" bg-color="transparent" class="pa-0" style="padding-left: 40px !important;">
              <v-list-item
                v-for="tag in curatedTags"
                :key="tag.id"
                :prepend-icon="tag.icon"
                :title="`#${tag.id}`"
                :active="isTagActive(tag.id)"
                active-color="primary"
                class="rounded-lg mb-1"
                @click="handleTagClick(tag.id); drawer = false"
              ></v-list-item>

              <template v-if="user && customDiscovers.length > 0">
                <div class="text-overline text-grey-darken-2 px-4 mt-2 mb-1" style="font-size: 0.6rem;">My Discoveries</div>
                <v-list-item
                  v-for="(discover, index) in customDiscovers"
                  :key="discover.id || index"
                  prepend-icon="mdi-magnify"
                  :title="discover.name"
                  :active="isDiscoverActive(discover.query)"
                  active-color="primary"
                  class="rounded-lg mb-1"
                  @click="handleDiscoverClick(discover); drawer = false"
                ></v-list-item>
              </template>
            </v-list>
          </div>
        </v-expand-transition>

        <v-divider class="my-3 border-white-05"></v-divider>

        <!-- Footer links -->
        <v-list density="compact" bg-color="transparent" class="pa-0">
          <v-list-item density="compact" prepend-icon="mdi-information-outline" title="What is Gamedev.id" @click="router.push('/what-is'); drawer = false" class="rounded-lg text-caption"></v-list-item>
          <v-list-item density="compact" prepend-icon="mdi-help-circle-outline" title="FAQ" @click="router.push('/faq'); drawer = false" class="rounded-lg text-caption"></v-list-item>
          <v-list-item density="compact" prepend-icon="mdi-file-document-outline" title="Terms of Service" @click="router.push('/tos'); drawer = false" class="rounded-lg text-caption"></v-list-item>
          <v-list-item density="compact" prepend-icon="mdi-history" title="Changelog" @click="router.push('/changelog'); drawer = false" class="rounded-lg text-caption"></v-list-item>
        </v-list>

      </div>
    </v-navigation-drawer>

    <!-- Discover Right Drawer (shown on < lg screens via toggle) -->
    <v-navigation-drawer v-model="showDiscoverDrawer" location="right" temporary class="bg-surface backdrop-blur d-lg-none">
      <div class="pa-4 pt-6">
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="text-overline text-grey-darken-1 font-weight-black">Discover</div>
          <v-btn icon="mdi-arrow-right" size="small" variant="text" color="grey" @click="showDiscoverDrawer = false"></v-btn>
        </div>
        <div class="d-flex flex-column ga-1">
          <div
            v-for="tag in curatedTags"
            :key="tag.id"
            class="tag-text-item py-2 px-3 rounded-lg cursor-pointer transition-all d-flex align-center"
            :class="{ 'bg-primary-05 text-primary font-weight-black': isTagActive(tag.id), 'text-grey-lighten-1 hover-surface': !isTagActive(tag.id) }"
            @click="handleTagClick(tag.id); showDiscoverDrawer = false"
            style="gap: 12px !important;"
          >
            <v-icon :icon="tag.icon" size="small" :color="isTagActive(tag.id) ? 'primary' : 'grey'"></v-icon>
            <span class="font-weight-bold">#{{ tag.id }}</span>
          </div>
        </div>

        <template v-if="user">
          <div class="d-flex align-center justify-space-between mb-2 mt-8">
            <div class="text-overline text-grey-darken-1 font-weight-black">My Discoveries</div>
            <v-btn icon="mdi-plus" size="x-small" variant="text" color="grey" @click="showDiscoverDialog = true"></v-btn>
          </div>

          <div v-if="customDiscovers.length === 0" class="text-caption text-grey-darken-2 italic">
            No custom searches yet.
          </div>

          <div class="d-flex flex-column ga-1">
            <div
              v-for="(discover, index) in customDiscovers"
              :key="discover.id || index"
              class="tag-text-item py-2 px-3 rounded-lg cursor-pointer transition-all d-flex align-center group"
              :class="{ 'bg-primary-05 text-primary font-weight-black': isDiscoverActive(discover.query), 'text-grey-lighten-1 hover-surface': !isDiscoverActive(discover.query) }"
              @click="handleDiscoverClick(discover); showDiscoverDrawer = false"
              style="gap: 12px !important;"
            >
              <v-icon icon="mdi-magnify" size="x-small" :color="isDiscoverActive(discover.query) ? 'primary' : 'grey'"></v-icon>
              <span class="flex-grow-1 text-truncate font-weight-bold">{{ discover.name }}</span>
              <div class="action-buttons">
                <v-btn icon="mdi-pencil" size="x-small" variant="text" color="grey" @click.stop="editingDiscover = discover; showDiscoverDialog = true"></v-btn>
                <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click.stop="handleDeleteDiscover(discover)"></v-btn>
              </div>
            </div>
          </div>
        </template>
      </div>
    </v-navigation-drawer>

    <v-app-bar :class="['main-app-bar', { 'search-focused': showSearchOverlay }]" elevation="0" style="border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;">
      <!-- Mobile: burger + logo icon only -->
      <div class="d-flex align-center d-md-none">
        <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>
        <img src="/logo.png" alt="Logo" style="height: 26px; width: auto;" class="cursor-pointer" @click="goHome" />
      </div>

      <!-- Desktop: logo + text with sidebar offset -->
      <div
        class="cursor-pointer d-none d-md-flex align-center"
        style="padding-left: 80px;"
        @click="goHome"
      >
        <img src="/logo.png" alt="Logo" style="height: 28px; width: auto;" class="mr-2" />
        <span style="font-size: 1.5rem; letter-spacing: -0.5px; color: rgb(var(--v-theme-primary)); font-weight: 900 !important;">
          <span style="color: #DBA858;">#</span><span class="text-white">g</span><span style="color: #DBA858;">ame</span><span class="text-white">d</span><span style="color: #DBA858;">ev</span><span class="text-white">i</span><span style="color: #DBA858;">d</span>
        </span>
      </div>
      <v-spacer></v-spacer>

      <div 
        class="central-search-container d-none d-md-flex align-center" 
        style="width: 450px; position: relative; z-index: 10001;"
      >
        <v-text-field
          ref="searchInputRef"
          v-model="globalSearchQuery"
          placeholder="Search for posts or users"
          variant="solo-filled"
          density="compact"
          prepend-inner-icon="mdi-magnify"
          clearable
          hide-details
          rounded="pill"
          class="central-search-input"
          style="width: 100%;"
          @keyup.enter="handleGlobalSearch"
          @keyup.esc="showSearchOverlay = false"
          @focus="showSearchOverlay = true"
        ></v-text-field>

        <Teleport to="body">
          <v-fade-transition>
            <div v-if="showSearchOverlay" class="search-backdrop" @click="showSearchOverlay = false"></div>
          </v-fade-transition>

          <v-expand-transition>
            <div 
              v-if="showSearchOverlay" 
              class="search-overlay-dropdown bg-surface border border-white-05 rounded-xl shadow-2xl pa-4"
              :style="searchDropdownStyles"
              v-click-outside="() => showSearchOverlay = false"
            >
              <v-list v-if="globalSearchQuery && globalSearchQuery.trim()" bg-color="transparent" density="compact" class="pa-0 mb-4">
                <v-list-item @click="handleGlobalSearch" class="rounded-lg bg-surface-variant-subtle">
                  <template v-slot:prepend>
                    <v-icon icon="mdi-magnify" size="small" class="mr-3"></v-icon>
                  </template>
                  <v-list-item-title class="font-weight-bold">Search for "{{ globalSearchQuery }}"</v-list-item-title>
                </v-list-item>
              </v-list>

              <div v-if="userSearchResults.length > 0" class="mb-4">
                <span class="text-overline text-grey d-block mb-2">People</span>
                <v-list bg-color="transparent" class="pa-0">
                  <v-list-item
                    v-for="user in userSearchResults"
                    :key="user.did"
                    @click="handleViewProfile(user.handle); showSearchOverlay = false; globalSearchQuery = ''"
                    class="rounded-lg mb-1"
                  >
                    <template v-slot:prepend>
                      <v-avatar size="32" class="mr-3 border border-white-02">
                        <v-img v-if="user.avatar" :src="user.avatar"></v-img>
                        <v-icon v-else icon="mdi-account" size="small"></v-icon>
                      </v-avatar>
                    </template>
                    <v-list-item-title class="text-body-2 font-weight-bold">{{ user.displayName || user.handle }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption text-grey">@{{ user.handle }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </div>

              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-overline text-grey">Recent searches</span>
                <v-btn icon="mdi-close" variant="text" size="x-small" @click="showSearchOverlay = false"></v-btn>
              </div>
              
              <div v-if="recentSearches.length === 0" class="text-center py-6 text-grey text-caption opacity-50">
                No recent searches
              </div>
              <v-list v-else bg-color="transparent" density="compact" class="pa-0">
                <v-list-item 
                  v-for="(s, i) in recentSearches" 
                  :key="i"
                  @click="performRecentSearch(s)"
                  class="rounded-lg mb-1 search-history-item"
                >
                  <template v-slot:prepend>
                    <v-icon icon="mdi-history" size="small" class="mr-3 opacity-60"></v-icon>
                  </template>
                  <v-list-item-title class="text-body-2">{{ s }}</v-list-item-title>
                  <template v-slot:append>
                    <v-btn icon="mdi-close" variant="text" size="x-small" density="comfortable" class="opacity-40" @click.stop="removeRecentSearch(i)"></v-btn>
                  </template>
                </v-list-item>
              </v-list>
            </div>
          </v-expand-transition>
        </Teleport>
      </div>

      <v-spacer></v-spacer>
      
      <v-btn icon class="mr-1 d-none d-md-flex" @click="fetchRecentPosts" color="grey-lighten-1">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <template v-if="user">
        <v-menu v-model="chatMenuOpen" :close-on-content-click="false" location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props" class="mr-1" @click="selectedConvo = null; updateUnreadCounts()">
              <v-badge
                v-if="chatUnreadCount > 0"
                :content="chatUnreadCount"
                color="primary"
                overlap
              >
                <v-icon>mdi-message-outline</v-icon>
              </v-badge>
              <v-icon v-else>mdi-message-outline</v-icon>
            </v-btn>
          </template>
          <ChatConversation 
            v-if="selectedConvo" 
            :convo="selectedConvo" 
            @back="selectedConvo = null" 
            @read="handleReadEvent"
            @view-profile="(handle) => { handleViewProfile(handle); chatMenuOpen = false; }"
          />
          <ChatMenu v-else @select-convo="handleSelectConvo" />
        </v-menu>

        <v-menu :close-on-content-click="false" location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props" class="mr-2" @click="unreadCount = 0">
              <v-badge
                v-if="unreadCount > 0"
                :content="unreadCount"
                color="error"
                overlap
              >
                <v-icon>mdi-bell-outline</v-icon>
              </v-badge>
              <v-icon v-else>mdi-bell-outline</v-icon>
            </v-btn>
          </template>
          <NotificationsMenu 
            @close="" 
            @view-post="viewThreadByUri" 
            @view-profile="handleViewProfile"
          />
        </v-menu>
      </template>

      <v-btn icon class="d-md-none ml-1" @click="showMobileSearch = true">
        <v-icon icon="mdi-magnify"></v-icon>
      </v-btn>

      <v-btn v-if="!user" variant="text" @click="showLogin = true" class="mr-1 mr-md-0" :style="{ marginRight: $vuetify?.display?.mdAndUp ? '80px' : '4px' }">
        Login
      </v-btn>
      <v-menu v-else>
        <template v-slot:activator="{ props }">
          <v-btn variant="text" v-bind="props" class="text-none px-1 px-md-3" :style="{ marginRight: $vuetify?.display?.mdAndUp ? '80px' : '4px' }">
            <v-avatar size="24" :class="{ 'mr-2': $vuetify.display.smAndUp }" color="surface-variant">
              <v-img v-if="userProfile?.avatar" :src="userProfile.avatar"></v-img>
              <v-icon v-else icon="mdi-account-circle"></v-icon>
            </v-avatar>
            <span class="d-none d-sm-inline">{{ userProfile?.handle || user?.handle }}</span>
          </v-btn>
        </template>
        <v-list class="bg-surface rounded-lg border border-white-05">
          <v-list-item prepend-icon="mdi-account-circle" @click="handleViewProfile(undefined)">
            <v-list-item-title>My Profile</v-list-item-title>
          </v-list-item>
          <v-list-item prepend-icon="mdi-bookmark-multiple" @click="router.push('/bookmarks')">
            <v-list-item-title>Bookmarks</v-list-item-title>
          </v-list-item>
          <v-list-item prepend-icon="mdi-playlist-play" @click="router.push('/lists')">
            <v-list-item-title>Lists</v-list-item-title>
          </v-list-item>
          <template v-if="isAdmin()">
            <v-divider class="my-1 border-white-05"></v-divider>
            <v-list-item prepend-icon="mdi-domain" @click="router.push('/admin/studios')">
              <v-list-item-title>Manage Studios</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-pin" @click="router.push('/admin/pinned')">
              <v-list-item-title>Manage Announcements</v-list-item-title>
            </v-list-item>
            <v-list-item prepend-icon="mdi-image" @click="router.push('/admin/ads')">
              <v-list-item-title>Manage Ads</v-list-item-title>
            </v-list-item>
          </template>
          <v-divider class="my-1 border-white-05"></v-divider>
          <v-list-item prepend-icon="mdi-cog-outline" @click="showConfig = true">
            <v-list-item-title>Settings</v-list-item-title>
          </v-list-item>
          <v-divider class="my-1 border-white-05"></v-divider>
          <v-list-item @click="handleLogout">
            <template v-slot:prepend>
              <v-icon icon="mdi-logout" color="error"></v-icon>
            </template>
            <v-list-item-title class="text-error">Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-main class="bg-surface">
      <v-container fluid :class="isFocusMode ? 'px-0' : 'px-0 px-sm-4 px-md-8 px-lg-12'">
        <v-row no-gutters justify="center">
          <v-col cols="12" class="d-flex justify-center">
            <v-row class="w-100" :style="isFocusMode ? '' : 'max-width: 1200px;'">
              <!-- Left Sidebar Navigation -->
              <v-col v-if="!isFocusMode" cols="auto" class="d-none d-md-flex flex-column align-center pt-10" style="width: 100px; min-width: 100px; z-index: 100;">
                <div class="sidebar-container sticky-sidebar w-100 d-flex flex-column align-center">
                  <!-- Logo Icon / Home -->
                  <!-- <div class="mb-10 d-flex justify-center w-100">
                    <div class="logo-circle-btn cursor-pointer d-flex align-center justify-center rounded-circle" @click="goHome" style="width: 50px; height: 50px; background: rgba(219, 168, 88, 0.1); border: 2px solid #DBA858;">
                      <span style="color: #DBA858; font-weight: 900; font-size: 1.5rem;">#</span>
                    </div>
                  </div> -->

                  <!-- Navigation -->
                  <div class="d-flex flex-column align-center w-100 ga-6">
                    <div 
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/' || route.path.startsWith('/t/') }"
                      @click="goHome"
                      title="Feed"
                    >
                      <v-icon :icon="(route.path === '/' || route.path.startsWith('/t/')) ? 'mdi-home' : 'mdi-home-outline'" size="28" :color="(route.path === '/' || route.path.startsWith('/t/')) ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="(route.path === '/' || route.path.startsWith('/t/')) ? 'text-white' : 'text-grey'">Feed</span>
                    </div>

                    <div 
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/curated' }"
                      @click="router.push('/curated')"
                      title="Curated"
                    >
                      <v-icon :icon="route.path === '/curated' ? 'mdi-star' : 'mdi-star-outline'" size="28" :color="route.path === '/curated' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/curated' ? 'text-white' : 'text-grey'">Curated</span>
                    </div>

                    <div 
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path.includes('/note/') }"
                      @click="showMyNotes"
                      title="Stories"
                    >
                      <v-icon :icon="route.path.includes('/note/') ? 'mdi-book-open-variant' : 'mdi-book-open-variant-outline'" size="28" :color="route.path.includes('/note/') ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path.includes('/note/') ? 'text-white' : 'text-grey'">Stories</span>
                    </div>

                    <div
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/studios' }"
                      @click="router.push('/studios')"
                      title="Studios"
                    >
                      <v-icon :icon="route.path === '/studios' ? 'mdi-domain' : 'mdi-domain'" size="28" :color="route.path === '/studios' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/studios' ? 'text-white' : 'text-grey'">Studios</span>
                    </div>

                    <div
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/events' }"
                      @click="router.push('/events')"
                      title="Events"
                    >
                      <v-icon :icon="route.path === '/events' ? 'mdi-calendar' : 'mdi-calendar-outline'" size="28" :color="route.path === '/events' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/events' ? 'text-white' : 'text-grey'">Events</span>
                    </div>

                    <div
                      v-if="user"
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/bookmarks' }"
                      @click="router.push('/bookmarks')"
                      title="Saved"
                    >
                      <v-icon :icon="route.path === '/bookmarks' ? 'mdi-bookmark' : 'mdi-bookmark-outline'" size="28" :color="route.path === '/bookmarks' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/bookmarks' ? 'text-white' : 'text-grey'">Saved</span>
                    </div>

                    <div 
                      v-if="user"
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/lists' }"
                      @click="router.push('/lists')"
                      title="Lists"
                    >
                      <v-icon :icon="route.path === '/lists' ? 'mdi-playlist-play' : 'mdi-playlist-play'" size="28" :color="route.path === '/lists' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/lists' ? 'text-white' : 'text-grey'">Lists</span>
                    </div>

                    <div
                      v-if="showTimeline"
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path === '/timeline' }"
                      @click="router.push('/timeline')"
                      title="Timeline"
                    >
                      <v-icon :icon="route.path === '/timeline' ? 'mdi-timeline' : 'mdi-timeline-outline'" size="28" :color="route.path === '/timeline' ? 'white' : 'grey'"></v-icon>
                      <span class="text-tiny font-weight-black mt-1" :class="route.path === '/timeline' ? 'text-white' : 'text-grey'">Timeline</span>
                    </div>

                    <div 
                      v-if="user"
                      class="nav-item-compact d-flex flex-column align-center cursor-pointer group"
                      :class="{ 'active-link': route.path.startsWith('/profile/') && !route.path.includes('/note/') && route.params.handle === user.handle }"
                      @click="handleViewProfile()"
                      title="Profile"
                    >
                      <v-avatar size="28" class="mb-1" :class="{ 'border-2 border-white': route.path.startsWith('/profile/') && !route.path.includes('/note/') && route.params.handle === user.handle }">
                        <v-img v-if="userProfile?.avatar" :src="userProfile.avatar"></v-img>
                        <v-icon v-else icon="mdi-account" color="grey"></v-icon>
                      </v-avatar>
                      <span class="text-tiny font-weight-black" :class="route.path.startsWith('/profile/') && !route.path.includes('/note/') && route.params.handle === user.handle ? 'text-white' : 'text-grey'">Profile</span>
                    </div>
                  </div>

                  <v-spacer></v-spacer>
                </div>
              </v-col>

              <!-- Main Content Viewport -->
              <v-col 
                cols="12" 
                :md="isFocusMode ? 12 : 9" 
                :lg="isFocusMode ? 12 : 7"
                :xl="isFocusMode ? 12 : 7"
                class="px-2 px-md-4"
                :class="{ 'mx-auto': isFocusMode }"
                :style="''"
              >
                <router-view v-slot="{ Component }">
                  <v-fade-transition mode="out-in">
                    <component 
                      :is="Component"
                      ref="viewRef"
                      @view-profile="handleViewProfile"
                      @view-thread="viewThread"
                      @tag-click="handleTagClick"
                      @back="router.back()"
                      @share-note="handleShareNote"
                      @activate-discussion="handleActivateDiscussion"
                      @message="handleStartChat"
                      @login="showLogin = true"
                      @quote="handleQuote"
                      @convert-to-note="handleConvertToNote"
                      @save-discovery="handleSaveSearchToDiscovery"
                    />
                  </v-fade-transition>
                </router-view>
              </v-col>

              <!-- Discover toggle (visible on < lg when sidebar is hidden) -->
              <v-btn
                v-if="!isFocusMode"
                icon
                size="small"
                variant="tonal"
                :color="showDiscoverDrawer ? 'primary' : 'grey'"
                class="d-lg-none"
                style="position: fixed; top: 72px; right: 12px; z-index: 99;"
                @click="showDiscoverDrawer = !showDiscoverDrawer"
                title="Discover"
              >
                <v-icon icon="mdi-pound"></v-icon>
              </v-btn>

              <!-- Right Sidebar (Discoveries) -->
              <v-col v-if="!isFocusMode" cols="12" lg="3" class="d-none d-lg-flex pt-10">
                <div class="sidebar-container sticky-sidebar pl-4 w-100">
                  <div class="text-overline mb-4 text-grey-darken-1 pl-3 font-weight-black">Discover</div>
                  <div class="d-flex flex-column ga-1">
                    <div
                      v-for="tag in curatedTags"
                      :key="tag.id"
                      class="tag-text-item py-2 px-3 rounded-lg cursor-pointer transition-all d-flex align-center"
                      :class="{ 'bg-primary-05 text-primary font-weight-black': isTagActive(tag.id), 'text-grey-lighten-1 hover-surface': !isTagActive(tag.id) }"
                      @click="handleTagClick(tag.id)"
                      style="gap: 12px !important;"
                    >
                      <v-icon :icon="tag.icon" size="small" :color="isTagActive(tag.id) ? 'primary' : 'grey'"></v-icon>
                      <span class="font-weight-bold">#{{ tag.id }}</span>
                    </div>
                  </div>

                  <template v-if="user">
                    <div
                      class="d-flex align-center justify-space-between mt-6 pl-3 cursor-pointer"
                      :class="myDiscoveriesExpanded ? 'mb-2' : 'mb-0'"
                      @click="myDiscoveriesExpanded = !myDiscoveriesExpanded"
                    >
                      <div class="text-overline text-grey-darken-1 font-weight-black">My Discoveries</div>
                      <div class="d-flex align-center">
                        <v-btn icon="mdi-plus" size="x-small" variant="text" color="grey" @click.stop="showDiscoverDialog = true"></v-btn>
                        <v-icon
                          :icon="myDiscoveriesExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                          size="small"
                          color="grey"
                        ></v-icon>
                      </div>
                    </div>

                    <template v-if="myDiscoveriesExpanded">
                      <div v-if="customDiscovers.length === 0" class="text-caption text-grey-darken-2 pl-3 italic">
                        No custom searches yet.
                      </div>
                      <div class="d-flex flex-column ga-1">
                        <div
                          v-for="(discover, index) in customDiscovers"
                          :key="discover.id || index"
                          class="tag-text-item py-2 px-3 rounded-lg cursor-pointer transition-all d-flex align-center group"
                          :class="{ 'bg-primary-05 text-primary font-weight-black': isDiscoverActive(discover.query), 'text-grey-lighten-1 hover-surface': !isDiscoverActive(discover.query) }"
                          @click="handleDiscoverClick(discover)"
                          style="gap: 12px !important;"
                        >
                          <v-icon icon="mdi-magnify" size="x-small" :color="isDiscoverActive(discover.query) ? 'primary' : 'grey'"></v-icon>
                          <span class="flex-grow-1 text-truncate font-weight-bold">{{ discover.name }}</span>
                          <div class="action-buttons">
                            <v-btn icon="mdi-pencil" size="x-small" variant="text" color="grey" @click.stop="editingDiscover = discover; showDiscoverDialog = true"></v-btn>
                            <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click.stop="handleDeleteDiscover(discover)"></v-btn>
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>

                  <!-- Ad slot -->
                  <div v-if="currentAd" style="margin-top: 48px;">
                    <a :href="currentAd.link" target="_blank" rel="noopener noreferrer" class="d-block">
                      <v-img
                        :src="currentAdImageUrl"
                        class="rounded-xl"
                        cover
                        style="max-height: 200px;"
                      ></v-img>
                    </a>
                  </div>

                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Fixed Footer Links at Bottom-Left -->
    <div class="fixed-footer d-none d-md-flex flex-column align-center text-center">
      <span class="text-tiny font-weight-medium text-grey cursor-pointer hover-text-white mb-2" @click="router.push('/what-is')">What is?</span>
      <span class="text-tiny font-weight-medium text-grey cursor-pointer hover-text-white mb-2" @click="router.push('/faq')">FAQ</span>
      <span class="text-tiny font-weight-medium text-grey cursor-pointer hover-text-white mb-2" @click="router.push('/tos')">Terms</span>
      <span class="text-tiny font-weight-medium text-grey cursor-pointer hover-text-white mb-2" @click="router.push('/changelog')">v1.3.0</span>
      <span class="text-tiny text-grey-darken-2 mt-2 opacity-50" style="font-size: 0.6rem !important;">gdev.id ©2026</span>
    </div>

    <!-- Config Modal -->
    <ConfigDialog v-model="showConfig" />

    <!-- Login Modal -->
    <LoginDialog v-model="showLogin" @login-success="onLoginSuccess" />
    <DiscoverDialog v-model="showDiscoverDialog" :editing-discover="editingDiscover" @save="handleSaveDiscover" />

    <!-- Shared Modals -->
    <v-dialog v-model="showShareDialog" max-width="600px">
      <v-card class="rounded-xl bg-surface border border-white-05">
        <v-card-title class="font-weight-black pa-4 pb-0 d-flex align-center justify-space-between">
          Share Story
          <v-btn icon="mdi-close" variant="text" size="small" @click="showShareDialog = false"></v-btn>
        </v-card-title>
        <v-card-text class="pa-4 pt-2">
          <CreatePost 
            :title="shareTitle" :footnote="shareFootnote" :initial-text="shareText" :initial-tags="shareTags"
            @success="handleShareNoteSuccess" @convert-to-note="handleConvertToNote"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showQuoteDialog" max-width="700px">
      <v-card class="rounded-xl bg-surface border border-white-05">
        <v-card-title class="font-weight-black pa-4 pb-0 d-flex align-center justify-space-between">
          Quote Post
          <v-btn icon="mdi-close" variant="text" size="small" @click="showQuoteDialog = false"></v-btn>
        </v-card-title>
        <v-card-text class="pa-2 px-md-4 pt-2">
          <CreatePost :quote="postToQuote" @success="handleQuoteSuccess" @remove-quote="showQuoteDialog = false" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteConfirm" max-width="400px">
      <v-card class="rounded-xl border border-white-05 bg-surface">
        <v-card-title class="text-h5 font-weight-black pa-6">Delete Discovery?</v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">
          Are you sure you want to delete "#{{ discoverToDelete?.name }}"? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="rounded-pill font-weight-bold px-6" @click="confirmDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed, nextTick } from 'vue';
import { showTimeline, vuetifyTheme, loadAppConfigFromPds } from '@/composables/useAppConfig';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@unhead/vue';
import { 
  currentUser,
  userProfile,
  fetchUserProfile,
  logout,
  getUnreadCount,
  getUnreadChatCount,
  customDiscovers,
  getDiscovers,
  saveDiscover,
  deleteDiscover,
  deletePost,
  isFocusMode,
  searchActors,
  isAdmin,
  getActiveAds,
  resolveAdImageUrl,
  getAdminDid,
  type Ad
} from '@/services/bluesky';
import type { CustomDiscover } from '@/services/bluesky';

// Components
import ConfigDialog from '@/components/ConfigDialog.vue';
import LoginDialog from '@/components/LoginDialog.vue';
import CreatePost from '@/components/CreatePost.vue';
import DiscoverDialog from '@/components/DiscoverDialog.vue';
import NotificationsMenu from '@/components/NotificationsMenu.vue';
import ChatMenu from './components/ChatMenu.vue';
import ChatConversation from './components/ChatConversation.vue';

const route = useRoute();
const router = useRouter();

// Authentication State
const user = currentUser;

// Theme
const currentVuetifyTheme = computed(vuetifyTheme);

// Config dialog
const showConfig = ref(false);

// UI State
const drawer = ref(false);
const showDiscoverDrawer = ref(false);
const myDiscoveriesExpanded = ref(false);
const mobileDiscoverOpen = ref(false);

// Ad state
const currentAd = ref<Ad | null>(null);
const currentAdImageUrl = ref('');

const loadAd = async () => {
  try {
    const ads = await getActiveAds();
    if (ads.length === 0) return;
    const ad = ads[Math.floor(Math.random() * ads.length)];
    currentAd.value = ad;
    const adminDid = await getAdminDid();
    currentAdImageUrl.value = resolveAdImageUrl(ad, adminDid);
  } catch (e) {
    console.error('[Ads] Failed to load ad:', e);
  }
};
const showLogin = ref(false);
const showDiscoverDialog = ref(false);
const editingDiscover = ref<CustomDiscover | null>(null);
const showDeleteConfirm = ref(false);
const discoverToDelete = ref<CustomDiscover | null>(null);
const showShareDialog = ref(false);
const shareText = ref('#gamedevid ');
const shareTitle = ref('');
const shareFootnote = ref('');
const shareTags = ref<string[]>(['gamedevid']);
const showQuoteDialog = ref(false);
const postToQuote = ref<any>(null);
const activatingNoteUri = ref('');
const viewRef = ref<any>(null);

// Notifications & Chat State
const unreadCount = ref(0);
const chatUnreadCount = ref(0);
const chatMenuOpen = ref(false);
const selectedConvo = ref<any>(null);
let pollingInterval: any = null;

const searchInputRef = ref<any>(null);
const searchDropdownStyles = ref<any>({});
const showMobileSearch = ref(false);

const updateSearchDropdownPosition = () => {
  if (searchInputRef.value?.$el) {
    const rect = searchInputRef.value.$el.getBoundingClientRect();
    searchDropdownStyles.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 10006
    };
  }
};

const globalSearchQuery = ref('');
const showSearchOverlay = ref(false);
const recentSearches = ref<string[]>(JSON.parse(localStorage.getItem('recent_searches') || '[]'));
const userSearchResults = ref<any[]>([]);

const curatedTags = [
  { id: 'gamedevid', icon: 'mdi-home' },
  { id: 'gdiprog', icon: 'mdi-code-tags' },
  { id: 'gdiengine', icon: 'mdi-engine' },
  { id: 'gdiart', icon: 'mdi-palette' },
  { id: 'gdidesign', icon: 'mdi-pencil-ruler' },
  { id: 'gdidevlog', icon: 'mdi-notebook-edit' },
  { id: 'gdirelease', icon: 'mdi-rocket-launch' },
  { id: 'gdicollab', icon: 'mdi-account-group' },
  { id: 'gdihelp', icon: 'mdi-help-circle' },
  { id: 'gdijob', icon: 'mdi-briefcase' },
  { id: 'gdinote', icon: 'mdi-note-text' },
];

const isTagActive = (tagId: string) => route.path === `/t/${tagId}` || (tagId === 'gamedevid' && route.path === '/');
const isDiscoverActive = (query: string) => route.path === '/search' && route.query.q === query;

// --- Core Logic ---

const updateUnreadCounts = async () => {
  if (user.value) {
    try {
      const [notifications, chats] = await Promise.all([getUnreadCount(), getUnreadChatCount()]);
      unreadCount.value = notifications;
      chatUnreadCount.value = chats;
    } catch (e) { console.error('Polling error:', e); }
  }
};

const startPolling = () => {
  stopPolling();
  updateUnreadCounts();
  pollingInterval = setInterval(updateUnreadCounts, 30000);
};

const stopPolling = () => { if (pollingInterval) clearInterval(pollingInterval); pollingInterval = null; };

// --- Navigation ---

const goHome = () => router.push('/');

const handleTagClick = (tag: string) => router.push(tag === 'gamedevid' ? '/' : `/t/${tag}`);

const handleDiscoverClick = (discover: CustomDiscover) => {
  router.push(`/search?q=${encodeURIComponent(discover.query)}`);
};

const handleViewProfile = (actor?: string) => {
  if (actor) {
    router.push(`/profile/${actor}`);
  } else if (user.value) {
    router.push(`/profile/${user.value.did}`);
  }
};

const showMyNotes = () => {
  if (!user.value) { showLogin.value = true; return; }
  router.push(`/profile/${user.value.handle}/note/`);
};

const fetchRecentPosts = () => {
  router.replace({ path: route.path, query: { ...route.query, _refresh: Date.now() } });
};

// --- Event Handlers ---

const handleGlobalSearch = () => {
  if (!globalSearchQuery.value.trim()) return;
  const query = globalSearchQuery.value.trim();
  if (!recentSearches.value.includes(query)) {
    recentSearches.value.unshift(query);
    recentSearches.value = recentSearches.value.slice(0, 5);
    localStorage.setItem('recent_searches', JSON.stringify(recentSearches.value));
  }
  showSearchOverlay.value = false;
  router.push(`/search?q=${encodeURIComponent(query)}`);
  globalSearchQuery.value = '';
};

const performRecentSearch = (query: string) => {
  globalSearchQuery.value = query;
  handleGlobalSearch();
};

const removeRecentSearch = (index: number) => {
  recentSearches.value.splice(index, 1);
  localStorage.setItem('recent_searches', JSON.stringify(recentSearches.value));
};

const onLoginSuccess = () => {
  showLogin.value = false;
  fetchUserProfile();
  getDiscovers();
  loadAppConfigFromPds();
  startPolling();
};

const handleLogout = () => {
  logout();
  stopPolling();
  goHome();
};

const handleSelectConvo = (convo: any) => { selectedConvo.value = convo; };

const handleReadEvent = () => { updateUnreadCounts(); };

const handleSaveDiscover = async (discover: CustomDiscover) => {
  try {
    const d = { ...discover };
    if (editingDiscover.value?.rkey) {
      d.rkey = editingDiscover.value.rkey;
      d.uri = editingDiscover.value.uri;
    }
    await saveDiscover(d);
    showDiscoverDialog.value = false;
    editingDiscover.value = null;
  } catch (e) {
    console.error('Save discover failed:', e);
  }
};

const handleDeleteDiscover = (discover: CustomDiscover) => {
  discoverToDelete.value = discover;
  showDeleteConfirm.value = true;
};

const confirmDelete = async () => {
  if (!discoverToDelete.value?.uri) return;
  try {
    await deleteDiscover(discoverToDelete.value.uri);
    showDeleteConfirm.value = false;
    discoverToDelete.value = null;
  } catch (e) {
    console.error('Delete discover failed:', e);
  }
};

const handleDelete = async (post: any) => {
  try { await deletePost(post.uri); } 
  catch (e) { console.error('Delete failed:', e); }
};

const viewThread = (post: any) => {
  const rkey = post.uri.split('/').pop();
  router.push(`/profile/${post.author.handle}/post/${rkey}`);
};

const viewThreadByUri = (uri: string) => {
  const parts = uri.split('/');
  const rkey = parts.pop();
  const handle = parts[parts.indexOf('app.bsky.feed.post') - 1] || parts[2];
  router.push(`/profile/${handle}/post/${rkey}`);
};

const handleQuote = (post: any) => {
  postToQuote.value = post;
  showQuoteDialog.value = true;
};

const handleQuoteSuccess = () => { showQuoteDialog.value = false; postToQuote.value = null; };

const handleShareNote = (note: any) => {
  const rkey = note.rkey || note.uri?.split('/').pop();
  shareTitle.value = note.value?.title || '';
  shareText.value = `New story on #gamedevid: ${note.value?.title || ''}\n\nRead full story here: ${window.location.origin}/profile/${note.author?.handle}/note/${rkey}`;
  shareFootnote.value = '';
  shareTags.value = ['gamedevid', 'gdidevlog'];
  showShareDialog.value = true;
};

const handleShareNoteSuccess = async (uri: string) => { 
  showShareDialog.value = false; 
  if (activatingNoteUri.value && viewRef.value?.confirmDiscussionActivation) {
    await viewRef.value.confirmDiscussionActivation(activatingNoteUri.value, uri);
    activatingNoteUri.value = '';
  }
};

const handleActivateDiscussion = (note: any) => {
  activatingNoteUri.value = note.uri;
  const rkey = note.rkey || note.uri?.split('/').pop();
  shareTitle.value = note.value?.title || '';
  shareText.value = `Discussion for: ${note.value?.title || ''}\n\nSee story context: ${window.location.origin}/profile/${note.author?.handle}/note/${rkey}`;
  shareFootnote.value = '';
  shareTags.value = ['gamedevid', 'gdinote'];
  showShareDialog.value = true;
};

const handleStartChat = (profile: any) => {
  chatMenuOpen.value = true;
  selectedConvo.value = { 
    members: [profile],
    id: 'new', // ChatMenu/Conversation should handle new convo creation or lookup
    rev: ''
  };
};

const handleConvertToNote = (text: string) => {
  router.push({ path: `/profile/${user.value?.handle}/note/`, query: { initialText: text } });
};

const handleSaveSearchToDiscovery = (query: string) => {
  editingDiscover.value = { id: Math.random().toString(36).substring(2, 9), name: '', query };
  showDiscoverDialog.value = true;
};

// Lifecycle
onMounted(() => {
  fetchUserProfile();
  getDiscovers();
  loadAd();
  if (user.value) loadAppConfigFromPds();
});

watch(() => route.path, () => {
  loadAd();
});

watch(user, (newUser, oldUser) => {
  if (newUser && !oldUser) startPolling();
  else if (!newUser && oldUser) stopPolling();
});

onUnmounted(stopPolling);

// Global SEO (Fallback)
useHead({ title: 'Gamedev.id' });

watch(showSearchOverlay, (val) => {
  if (val) {
    updateSearchDropdownPosition();
    window.addEventListener('resize', updateSearchDropdownPosition);
    window.addEventListener('scroll', updateSearchDropdownPosition, true);
  } else {
    window.removeEventListener('resize', updateSearchDropdownPosition);
    window.removeEventListener('scroll', updateSearchDropdownPosition, true);
  }
});

watch(globalSearchQuery, async (newQuery) => {
  if (newQuery && newQuery.trim()) {
    userSearchResults.value = await searchActors(newQuery.trim(), 5);
  } else {
    userSearchResults.value = [];
  }
});
</script>

<style>
.sidebar-container {
  height: calc(100vh - 100px);
  overflow-y: auto;
  scrollbar-width: none;
}
.sidebar-container::-webkit-scrollbar {
  display: none;
}
.sticky-sidebar {
  position: sticky;
  top: 80px;
}
.fixed-footer {
  position: fixed;
  bottom: 24px;
  left: 24px;
  z-index: 9999;
  width: auto;
  pointer-events: auto;
}
.fixed-footer span {
  pointer-events: auto;
  cursor: pointer;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.2s ease;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
}
.nav-item .v-icon {
  color: #fff !important;
}
.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.active-nav-item {
  background-color: rgba(219, 168, 88, 0.15) !important;
}
.active-nav-item .v-icon {
  color: #fff !important;
}
.active-nav-item span {
  color: #fff !important;
}
.tag-text-item {
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 8px;
  margin-bottom: 2px;
  gap: 12px;
}
.tag-text-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}
.group:hover .action-buttons {
  opacity: 1;
}
.action-buttons {
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  gap: 4px;
}
.info-link:hover {
  color: white !important;
  opacity: 1 !important;
}
.hover-white:hover {
  color: white !important;
}
.text-tiny {
  font-size: 0.7rem;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.bg-primary-05 {
  background-color: rgba(219, 168, 88, 0.05) !important;
}
</style>
